import {
  AtSignIcon,
  BookmarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CircleHelpIcon,
  CompassIcon,
  InboxIcon,
  KeyboardIcon,
  LogOutIcon,
  MessageSquareTextIcon,
  PanelLeftIcon,
  PlusIcon,
  RefreshCwIcon,
  SearchIcon,
  SettingsIcon,
  SquarePenIcon,
  SunMoonIcon,
  type LucideIcon,
} from "lucide-react"
import { useEffect, useState, type ReactNode } from "react"
import { Link, NavLink, useLocation, useMatch } from "react-router"
import type { ConversationSummary, MemberPresence } from "@beacon/core/chat"
import type { Project } from "@beacon/core/projects"
import { useQuery } from "@tanstack/react-query"
import { channelIconOf, isDirect, privacyOf } from "@/chat/channel-glyph.ts"
import { attentionQuery, isMuted } from "@/chat/attention.ts"
import { inboxUnreadQuery } from "@/beacon-inbox/client.ts"
import { describeInboxUnread } from "@/beacon-inbox/inbox.ts"
import { conversationsQuery, unreadsQuery } from "@/chat/client.ts"
import { placeOf } from "@/chat/describe.ts"
import { describeMentionsUnread } from "@/chat/mentions.ts"
import {
  conversationHref,
  describeThreadsUnread,
  describeUnread,
  unreadCountShown,
} from "@/chat/unread.ts"
import { StatusMark, usePresence, useStatusShown } from "@/chat/presence-marks.tsx"
import { MutedMark } from "@/chat/unread-marks.tsx"
import { describePresence, describeStatus } from "@/chat/presence.ts"
import {
  Avatar,
  ColourSchemeToggle,
  Disc,
  IdentityTile,
  Kbd,
  RoleGate,
  useViewport,
} from "@/design-system/index.ts"
import { installIcon, useInstallOffer } from "@/installed-app/install-offer.tsx"
import { reloadToUpdate, sayElsewhere, useUpdate } from "@/installed-app/updates.ts"
import { Button } from "@/primitives/button.tsx"
import { Drawer, DrawerContent, DrawerTitle } from "@/primitives/drawer.tsx"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  useSidebar,
} from "@/primitives/sidebar.tsx"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/primitives/tooltip.tsx"
import { cn } from "@/primitives/utils"
import { projectsByName } from "@/projects/order.ts"
import type { ShortcutId } from "@/shortcuts/registry.ts"
import { useShortcut } from "@/shortcuts/use-shortcut.ts"
import type { Me } from "@/sign-in/client.ts"
import { AccountMenu } from "./account-menu.tsx"
import { describeStanding } from "./describe.ts"
import {
  adminDestinations,
  designSystem,
  destinations,
  inAdministration,
  type Destination,
} from "./destinations.ts"
import { QueryDevtools } from "./query-devtools.tsx"
import { chatRowCurrent } from "./chat-row.ts"
import { readFolds, writeFold, type SidebarSection } from "./sections.ts"

/*
 * The sidebar, on the canvas beside the page: the Member's own row that opens the account menu, the
 * command palette's search, the pages every Member has (their Beacon inbox among them, and Chat with
 * its Threads, Mentions and Saved beneath it), the Chat channels they have joined, their direct
 * messages, their Projects with My Project first, and at the foot the shortcuts and the collapse
 * toggle. On an Administration page an Admin sees it
 * turned to Administration instead: the way back to where they were, and the Admin's pages. The
 * current page sits on a grey fill; nav is never teal. Collapsed to icons, each row names itself
 * and its G-chord in a tooltip.
 *
 * On the compact layout it is the avatar's drawer instead, a bottom sheet the tab bar opens: who is
 * signed in, "Update Beacon" while a new release waits, the pages the tabs don't hold, the
 * Projects, and the account menu's items laid out as rows (the colour scheme as a control in its
 * row, and "Install Beacon" where the browser can). The tab bar owns Search, so the drawer has no
 * search of its own, and it has nothing to collapse. It closes as any route is taken.
 */

/** How many of a Member's Projects the sidebar lists before "All Projects". */
const projectsShown = 8

/**
 * A page in the sidebar. A `nested` one stays current on the pages beneath it too, unless
 * `elsewhere` says another row is the current one there. Its own pages' rows (`children`) sit
 * beneath it.
 */
function DestinationItem({
  destination: { to, title, Icon, chord, nested },
  badge,
  elsewhere = false,
  children,
}: {
  destination: Omit<Destination, "chord"> & { chord?: ShortcutId }
  badge?: number
  elsewhere?: boolean
  children?: ReactNode
}) {
  const current = useMatch({ path: to, end: !nested }) && !elsewhere
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        render={<NavLink to={to} end={!nested} />}
        isActive={Boolean(current)}
        tooltip={{
          children: (
            <>
              {title}
              {chord && <Kbd shortcut={chord} />}
            </>
          ),
        }}
      >
        <Icon />
        <span>{title}</span>
      </SidebarMenuButton>
      {badge !== undefined && badge > 0 && <SidebarMenuBadge>{badge}</SidebarMenuBadge>}
      {children}
    </SidebarMenuItem>
  )
}

function ProjectItem({ project: { key, name } }: { project: Project }) {
  const current = useMatch(`/projects/${key}/*`)
  return (
    <SidebarMenuItem>
      <SidebarMenuButton render={<NavLink to={`/projects/${key}`} />} isActive={Boolean(current)}>
        <IdentityTile projectKey={key} size={16} />
        <span>{name}</span>
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}

/**
 * Whether a section is open, and the toggle that folds or opens it and remembers so in this browser.
 */
function useFold(section: SidebarSection) {
  const [open, setOpen] = useState(() => !readFolds()[section])
  function toggle() {
    setOpen(!open)
    writeFold(section, open)
  }
  return [open, toggle] as const
}

/**
 * A section's label as the button that folds it, with a chevron shown on hover, and to a keyboard
 * as it reaches the label, so the fold is seen both ways. Hidden on the icon rail, which has no
 * labels. On touch it is 44px tall, as a row is, since a hit area reaching past it would take the
 * top of the row below.
 */
function FoldLabel({
  open,
  onToggle,
  children,
}: {
  open: boolean
  onToggle: () => void
  children: string
}) {
  return (
    <SidebarGroupLabel
      render={<button type="button" onClick={onToggle} aria-expanded={open} />}
      className="gap-1 hover:text-sidebar-foreground group-data-[collapsible=icon]:hidden touch:h-11 [&>svg]:size-3.5"
    >
      {children}
      <ChevronRightIcon
        aria-hidden
        className={
          open
            ? "rotate-90 transition duration-100 reveal-on-hover-sidebar-group"
            : "transition duration-100 reveal-on-hover-sidebar-group"
        }
      />
    </SidebarGroupLabel>
  )
}

/**
 * My Project first, a row like the rest, then the Member's other Projects, up to eight by
 * name, then a link to the rest when there are more (the Projects page is a row above, so it isn't
 * repeated when every Project is listed). The list folds away under its label and offers a new
 * Project on hover. My Project stays when the list is folded and when the
 * sidebar is collapsed to icons, where the rest of the section is hidden.
 */
function ProjectsSection({
  projects,
  myProject,
}: {
  projects: readonly Project[]
  myProject: Destination | undefined
}) {
  const [open, toggle] = useFold("projects")
  const team = projects.filter((project) => !project.personal)
  const listed = projectsByName(team).slice(0, projectsShown)

  return (
    <SidebarGroup>
      <FoldLabel open={open} onToggle={toggle}>
        Projects
      </FoldLabel>
      <Tooltip>
        <TooltipTrigger
          render={
            <SidebarGroupAction
              render={<Link to="/projects?new=1" aria-label="New Project" />}
              className="reveal-on-hover-sidebar-group group-data-[collapsible=icon]:hidden"
            />
          }
        >
          <PlusIcon />
        </TooltipTrigger>
        <TooltipContent side="right">New Project</TooltipContent>
      </Tooltip>
      <SidebarGroupContent>
        <SidebarMenu>{myProject && <DestinationItem destination={myProject} />}</SidebarMenu>
      </SidebarGroupContent>
      {open && (
        <SidebarGroupContent className="group-data-[collapsible=icon]:hidden">
          <SidebarMenu>
            {listed.map((project) => (
              <ProjectItem key={project.key} project={project} />
            ))}
            {team.length > listed.length && (
              <SidebarMenuItem>
                <SidebarMenuButton
                  render={<Link to="/projects" />}
                  className="text-subtle-foreground"
                >
                  <span>All Projects</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )}
          </SidebarMenu>
        </SidebarGroupContent>
      )}
    </SidebarGroup>
  )
}

/**
 * A channel the Member has joined, as a row opening its conversation (at its first unread message,
 * when it has one): a private one by a lock, one with something unread in bold, said so after its
 * name. One the Member muted is never bold, its name dimmed and marked with a struck bell.
 */
function ChannelItem({
  conversation,
  muted,
}: {
  conversation: ConversationSummary
  muted: boolean
}) {
  const { id, kind, name, unread, archived } = conversation
  const current = useMatch(`/chat/${id}`)
  const Icon = channelIconOf(kind)
  const privacy = privacyOf(kind)
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        render={<NavLink to={conversationHref(conversation)} />}
        isActive={Boolean(current)}
        aria-label={rowNameOf([
          privacy ? `${privacy}: ${name ?? ""}` : (name ?? ""),
          archived && "archived",
          muted && "muted",
          unread > 0 && describeUnread(unread),
        ])}
        className={cn(emphasisOf(unread, muted), archived && "opacity-60")}
      >
        <Icon aria-hidden />
        <span>{name}</span>
        {muted && <MutedMark />}
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}

/** A conversation row's weight: bold while something is unread, dimmed while muted. */
const emphasisOf = (unread: number, muted: boolean) =>
  muted
    ? "text-subtle-foreground"
    : unread > 0
      ? "font-semibold text-sidebar-foreground"
      : undefined

/** A row's name for a screen reader: its parts, those it has, after one another. */
const rowNameOf = (parts: readonly (string | false | null | undefined)[]) =>
  parts.filter(Boolean).join(", ")

/**
 * One of Chat's own pages, as a row beneath Chat's: in bold with how many of its things the Member
 * hasn't read while any are (`unread`, which `named` says in words).
 */
function ChatPageItem({
  to,
  title,
  Icon,
  unread = 0,
  named,
}: {
  to: string
  title: string
  Icon: LucideIcon
  unread?: number
  named?: (unread: number) => string
}) {
  const current = useMatch(to)
  return (
    <SidebarMenuSubItem>
      <SidebarMenuSubButton
        render={<NavLink to={to} />}
        isActive={Boolean(current)}
        // Named whole, as words read after a hidden span pick up a stray space before the comma.
        {...(unread > 0 && named && { "aria-label": `${title}, ${named(unread)}` })}
        className={unread > 0 ? "font-semibold text-sidebar-foreground" : undefined}
      >
        <Icon aria-hidden />
        <span>{title}</span>
        {unread > 0 && (
          <span
            aria-hidden
            className="ml-auto px-1 text-2xs font-medium text-subtle-foreground tabular-nums"
          >
            {unreadCountShown(unread)}
          </span>
        )}
      </SidebarMenuSubButton>
    </SidebarMenuSubItem>
  )
}

/**
 * Chat's own pages, beneath its row: the threads the Member follows and the messages that mentioned
 * them, each with how many they haven't read, and their saved messages. Hidden on the icon rail.
 */
function ChatPages() {
  const unreads = useQuery(unreadsQuery).data
  return (
    <SidebarMenuSub className="gap-px">
      <ChatPageItem
        to="/chat/threads"
        title="Threads"
        Icon={MessageSquareTextIcon}
        unread={unreads?.threads}
        named={describeThreadsUnread}
      />
      <ChatPageItem
        to="/chat/mentions"
        title="Mentions"
        Icon={AtSignIcon}
        unread={unreads?.mentions}
        named={describeMentionsUnread}
      />
      <ChatPageItem to="/chat/saved" title="Saved" Icon={BookmarkIcon} />
    </SidebarMenuSub>
  )
}

/**
 * The Member's Beacon inbox, its work notices and catch-ups, as a row among the pages every Member
 * has, in bold with how many they haven't read while any are. Apart from their DMs, which people
 * write.
 */
function InboxItem() {
  const current = useMatch("/chat/inbox")
  const unread = useQuery(inboxUnreadQuery).data?.unread ?? 0
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        render={<NavLink to="/chat/inbox" />}
        isActive={Boolean(current)}
        tooltip="Inbox"
        // Named whole, as words read after a hidden span pick up a stray space before the comma.
        {...(unread > 0 && { "aria-label": `Inbox, ${describeInboxUnread(unread)}` })}
        className={unread > 0 ? "font-semibold text-sidebar-foreground" : undefined}
      >
        <InboxIcon aria-hidden />
        <span>Inbox</span>
      </SidebarMenuButton>
      {unread > 0 && <SidebarMenuBadge aria-hidden>{unreadCountShown(unread)}</SidebarMenuBadge>}
    </SidebarMenuItem>
  )
}

/**
 * A section's last row, a way to more of it rather than a place in it: an icon, as every row has, so
 * it doesn't read as a section's label.
 */
function WayItem({ to, Icon, children }: { to: string; Icon: LucideIcon; children: string }) {
  return (
    <SidebarMenuItem>
      <SidebarMenuButton render={<Link to={to} />}>
        <Icon aria-hidden />
        <span>{children}</span>
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}

/**
 * The channels the Member has joined, by name, under their label, which folds them away and offers
 * a new channel on hover, then a way to browse the rest. An
 * archived channel stays off it (the Chat page lists it), but for the one open now, dimmed, so the
 * row of the page the Member is on doesn't vanish as they archive it. Hidden on the icon rail,
 * where Chat's own row above leads to them.
 */
function ChannelsSection() {
  const [open, toggle] = useFold("channels")
  const shown = useMatch("/chat/:conversationId")?.params.conversationId
  const joined = (useQuery(conversationsQuery).data ?? []).filter(
    ({ id, kind, archived }) => !isDirect(kind) && (!archived || id === shown),
  )
  const attention = useQuery(attentionQuery).data
  return (
    <SidebarGroup className="group-data-[collapsible=icon]:hidden">
      <FoldLabel open={open} onToggle={toggle}>
        Channels
      </FoldLabel>
      <Tooltip>
        <TooltipTrigger
          render={
            <SidebarGroupAction
              render={<Link to="/chat?new=1" aria-label="New channel" />}
              className="reveal-on-hover-sidebar-group"
            />
          }
        >
          <PlusIcon />
        </TooltipTrigger>
        <TooltipContent side="right">New channel</TooltipContent>
      </Tooltip>
      {open && (
        <SidebarGroupContent>
          <SidebarMenu>
            {joined.map((conversation) => (
              <ChannelItem
                key={conversation.id}
                conversation={conversation}
                muted={isMuted(attention, conversation.id)}
              />
            ))}
            <WayItem to="/chat" Icon={CompassIcon}>
              Browse channels
            </WayItem>
          </SidebarMenu>
        </SidebarGroupContent>
      )}
    </SidebarGroup>
  )
}

/**
 * A direct message the Member is in, as a row opening it, named by who it is with: a one-to-one
 * one by that person's photo, with whether they are online (`seen`) and their status, a group one
 * by people.
 */
function DmItem({
  conversation,
  seen,
  muted,
}: {
  conversation: ConversationSummary
  seen?: MemberPresence | undefined
  muted: boolean
}) {
  const { id, kind, unread } = conversation
  const current = useMatch(`/chat/${id}`)
  const Icon = channelIconOf(kind)
  const other = kind === "dm" ? conversation.with[0] : undefined
  const status = useStatusShown(other ? seen?.status : null)
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        render={<NavLink to={conversationHref(conversation)} />}
        isActive={Boolean(current)}
        // Named whole, as words read after hidden spans pick up stray spaces before their commas.
        aria-label={rowNameOf([
          `${privacyOf(kind)}: ${placeOf(conversation)}`,
          other && seen && describePresence(seen.presence).toLowerCase(),
          status && `status: ${describeStatus(status)}`,
          muted && "muted",
          unread > 0 && describeUnread(unread),
        ])}
        className={emphasisOf(unread, muted)}
      >
        {other ? (
          <Avatar person={other} size={16} presence={seen?.presence} on="sidebar" decorative />
        ) : (
          <Icon aria-hidden />
        )}
        <span className="truncate">{placeOf(conversation)}</span>
        {other && seen && <StatusMark status={seen.status} />}
        {muted && <MutedMark />}
      </SidebarMenuButton>
      {/* A direct message alerts, so it says how many; a channel's bold is enough. Muted, neither. */}
      {unread > 0 && !muted && (
        <SidebarMenuBadge aria-hidden>{unreadCountShown(unread)}</SidebarMenuBadge>
      )}
    </SidebarMenuItem>
  )
}

/**
 * The Member's direct messages, the latest spoken in first, under their label, which folds them
 * away and offers a new message on hover. Hidden on the icon rail, as the channels are.
 */
function DmsSection() {
  const [open, toggle] = useFold("dms")
  const dms = (useQuery(conversationsQuery).data ?? []).filter(({ kind }) => isDirect(kind))
  // A one-to-one DM's person shows whether they are online, and their status.
  const seen = usePresence(
    dms.flatMap((dm) => (dm.kind === "dm" && dm.with[0] ? [dm.with[0].id] : [])),
  )
  const attention = useQuery(attentionQuery).data
  return (
    <SidebarGroup className="group-data-[collapsible=icon]:hidden">
      <FoldLabel open={open} onToggle={toggle}>
        Direct messages
      </FoldLabel>
      <Tooltip>
        <TooltipTrigger
          render={
            <SidebarGroupAction
              render={<Link to="/chat?dm=1" aria-label="New message" />}
              className="reveal-on-hover-sidebar-group"
            />
          }
        >
          <PlusIcon />
        </TooltipTrigger>
        <TooltipContent side="right">New message</TooltipContent>
      </Tooltip>
      {open && (
        <SidebarGroupContent>
          <SidebarMenu>
            {dms.map((conversation) => (
              <DmItem
                key={conversation.id}
                conversation={conversation}
                seen={conversation.with[0] && seen(conversation.with[0].id)}
                muted={isMuted(attention, conversation.id)}
              />
            ))}
            <WayItem to="/chat?dm=1" Icon={SquarePenIcon}>
              New message
            </WayItem>
          </SidebarMenu>
        </SidebarGroupContent>
      )}
    </SidebarGroup>
  )
}

/**
 * Administration, as the sidebar shows it on the Admin's pages: the row back to the page the Admin
 * came from, and the Admin's pages under their label, the design system's last.
 */
function AdministrationNav({ backTo }: { backTo: string }) {
  return (
    <>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              render={<Link to={backTo} />}
              tooltip="Back to Beacon"
              className="text-muted-foreground hover:text-foreground"
            >
              <ChevronLeftIcon />
              <span>Back to Beacon</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent role="navigation" aria-label="Administration">
        <SidebarGroup>
          <SidebarGroupLabel>Administration</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {[...adminDestinations, designSystem].map((destination) => (
                <DestinationItem key={destination.to} destination={destination} />
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </>
  )
}

/**
 * Where "Back to Beacon" goes: the last page the Member was on outside Administration, or Home when
 * they opened Beacon on an Administration page.
 */
function useBackTo(administration: boolean) {
  const { pathname, search } = useLocation()
  const here = `${pathname}${search}`
  const [backTo, setBackTo] = useState("/")
  if (!administration && backTo !== here) setBackTo(here)
  return backTo
}

/**
 * The foot: the shortcuts behind `?`, the cache inspector in development, and the toggle that
 * collapses the sidebar (⌘B too, which `AppSidebar` binds). The first two hide on the icon rail,
 * which has room for the toggle alone.
 */
function Foot({ onShowShortcuts }: { onShowShortcuts: () => void }) {
  const { state, toggleSidebar } = useSidebar()
  const collapsed = state === "collapsed"
  return (
    <SidebarFooter className="mt-auto flex-row items-center">
      <Tooltip>
        <TooltipTrigger
          render={
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Keyboard shortcuts"
              onClick={onShowShortcuts}
              className="group-data-[collapsible=icon]:hidden"
            />
          }
        >
          <CircleHelpIcon />
        </TooltipTrigger>
        <TooltipContent side="top">
          Keyboard shortcuts
          <Kbd shortcut="shortcuts" />
        </TooltipContent>
      </Tooltip>
      <span className="flex group-data-[collapsible=icon]:hidden">
        <QueryDevtools />
      </span>
      <Tooltip>
        <TooltipTrigger
          render={
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              onClick={toggleSidebar}
              className="ml-auto group-data-[collapsible=icon]:mr-auto"
            />
          }
        >
          <PanelLeftIcon />
        </TooltipTrigger>
        <TooltipContent side={collapsed ? "right" : "top"}>
          {collapsed ? "Expand sidebar" : "Collapse sidebar"}
          <Kbd shortcut="toggle-sidebar" />
        </TooltipContent>
      </Tooltip>
    </SidebarFooter>
  )
}

/**
 * The avatar's drawer on the compact layout: the sidebar's rows in a bottom sheet on the canvas,
 * opened from the tab bar through the sidebar's own mobile state, so ⌘B opens it too. Whatever
 * route is taken from it (or from anywhere), it closes.
 */
function AvatarDrawer({ me, children }: { me: Me; children: ReactNode }) {
  const { openMobile, setOpenMobile } = useSidebar()
  const { key } = useLocation()
  const update = useUpdate().state.status !== "current"
  useEffect(() => setOpenMobile(false), [key, setOpenMobile])
  // Open, the drawer leads with Update Beacon, and the notice would cover its last rows.
  useEffect(() => (openMobile && update ? sayElsewhere() : undefined), [openMobile, update])
  return (
    <Drawer open={openMobile} onOpenChange={setOpenMobile}>
      <DrawerContent className="bg-sidebar text-sidebar-foreground after:bg-sidebar">
        <DrawerTitle className="sr-only">Navigation</DrawerTitle>
        <div className="flex items-center gap-3 px-4 pt-1 pb-2">
          <Disc person={me} size={32} />
          <div className="min-w-0">
            <p className="truncate text-ui font-semibold">{me.name}</p>
            <p className="truncate text-xs text-sidebar-muted-foreground">{describeStanding(me)}</p>
          </div>
        </div>
        {/* The release the avatar's dot points at, first, where the dot leads the eye. */}
        {update && (
          <SidebarGroup className="pt-0">
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton onClick={reloadToUpdate} className="text-sidebar-foreground">
                  <RefreshCwIcon />
                  <span>Update Beacon</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroup>
        )}
        <div className="flex flex-col pb-2">{children}</div>
      </DrawerContent>
    </Drawer>
  )
}

/**
 * The account menu's items as the drawer's last rows: the pages a Member sets up once,
 * Administration for an Admin who isn't in it already, the colour scheme, installing Beacon where
 * the browser can, the shortcuts on a keyboard, and signing out.
 */
function AccountRows({
  me,
  administration,
  onShowShortcuts,
  onSignOut,
}: {
  me: Me
  administration: boolean
  onShowShortcuts: () => void
  onSignOut: () => void
}) {
  const { setOpenMobile } = useSidebar()
  const { offered, install } = useInstallOffer()
  const InstallIcon = installIcon(true)
  return (
    <SidebarGroup>
      <SidebarGroupLabel>Account</SidebarGroupLabel>
      <SidebarGroupContent>
        <SidebarMenu>
          {destinations
            .filter((destination) => destination.accountMenu)
            .map((destination) => (
              <DestinationItem key={destination.to} destination={destination} />
            ))}
          {/* Administration opens on its first page, turning the drawer to the Admin's pages. */}
          {!administration && (
            <RoleGate role={me.role} admits="admin">
              <SidebarMenuItem>
                <SidebarMenuButton render={<Link to={adminDestinations[0]?.to ?? "/"} />}>
                  <SettingsIcon />
                  <span>Administration</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </RoleGate>
          )}
          <SidebarMenuItem className="flex h-7 items-center gap-2 px-2 text-ui font-medium text-sidebar-muted-foreground touch:h-11 [&>svg]:size-4 [&>svg]:shrink-0">
            <SunMoonIcon aria-hidden />
            <span>Theme</span>
            <span className="ml-auto">
              <ColourSchemeToggle />
            </span>
          </SidebarMenuItem>
          {offered && (
            <SidebarMenuItem>
              {/* The drawer makes way for the browser's prompt, or the sheet with the steps. */}
              <SidebarMenuButton
                onClick={() => {
                  setOpenMobile(false)
                  install()
                }}
              >
                <InstallIcon />
                <span>Install Beacon</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
          <SidebarMenuItem className="touch:hidden">
            <SidebarMenuButton onClick={onShowShortcuts}>
              <KeyboardIcon />
              <span>Keyboard shortcuts</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={onSignOut}>
              <LogOutIcon />
              <span>Sign out</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  )
}

export function AppSidebar({
  me,
  projects,
  openTickets,
  onSearch,
  onShowShortcuts,
  onSignOut,
}: {
  me: Me
  /** The Member's Projects that aren't archived. */
  projects: readonly Project[]
  /** How many open tickets are assigned to the Member, once known. */
  openTickets: number | undefined
  onSearch: () => void
  onShowShortcuts: () => void
  onSignOut: () => void
}) {
  const [home, myTickets, ...others] = destinations
  // My Project leads the Projects section rather than sitting among these, and the account menu
  // lists its own.
  const shown = others.filter(
    (destination) => destination.to !== "/my-project" && !destination.accountMenu,
  )
  // Once their Personal Project is made, My Project goes straight to its Board, and stays current
  // on its pages; until then it goes to the page that makes it.
  const personal = projects.find((project) => project.personal)
  const myProjectPage = destinations.find((destination) => destination.to === "/my-project")
  const myProject =
    myProjectPage && personal
      ? { ...myProjectPage, to: `/projects/${personal.key}`, nested: true as const }
      : myProjectPage
  // On its pages My Project is the current row, not Projects, which holds every other Board.
  const onPersonal = Boolean(useMatch(`/projects/${personal?.key ?? "-"}/*`)) && Boolean(personal)
  // Administration's pages turn the sidebar to it for an Admin. A Member who isn't one keeps the
  // app's sidebar, beside the page refusing them.
  const { pathname } = useLocation()
  const administration = me.role === "admin" && inAdministration(pathname)
  const backTo = useBackTo(administration)
  const { compact } = useViewport()
  // ⌘B collapses the sidebar, or on the compact layout opens and closes the avatar's drawer. It is
  // bound through the registry like every other shortcut, so it leaves ⌘B to a field (bold, in the
  // markdown editor) and to a dialog open over the page.
  const { toggleSidebar, state } = useSidebar()
  useShortcut("toggle-sidebar", toggleSidebar)
  // Chat's row gives way to a row beneath it that holds the page: one of its own pages, or a
  // conversation listed.
  const conversations = useQuery(conversationsQuery).data
  const onChat = chatRowCurrent({
    pathname,
    rail: state === "collapsed",
    listed: new Set(conversations?.map(({ id }) => id)),
  })

  if (compact) {
    const account = (
      <AccountRows
        me={me}
        administration={administration}
        onShowShortcuts={onShowShortcuts}
        onSignOut={onSignOut}
      />
    )
    return (
      <AvatarDrawer me={me}>
        {administration ? (
          <AdministrationNav backTo={backTo} />
        ) : (
          <SidebarContent role="navigation" aria-label="Main" className="overflow-visible">
            {/* Home, My tickets, Chat and Projects are tabs; the drawer holds the rest. */}
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu>
                  {shown
                    .filter((destination) => !destination.tab)
                    .map((destination) => (
                      <DestinationItem key={destination.to} destination={destination} />
                    ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
            <ProjectsSection projects={projects} myProject={myProject} />
          </SidebarContent>
        )}
        {account}
      </AvatarDrawer>
    )
  }

  if (administration) {
    return (
      <Sidebar variant="inset" collapsible="icon">
        <AdministrationNav backTo={backTo} />
        <Foot onShowShortcuts={onShowShortcuts} />
        <SidebarRail />
      </Sidebar>
    )
  }

  return (
    <Sidebar variant="inset" collapsible="icon">
      <SidebarHeader>
        <div className="flex h-9 items-center gap-1">
          <AccountMenu me={me} onShowShortcuts={onShowShortcuts} onSignOut={onSignOut} />
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Search"
                  onClick={onSearch}
                  className="group-data-[collapsible=icon]:hidden"
                />
              }
            >
              <SearchIcon />
            </TooltipTrigger>
            <TooltipContent side="bottom">
              Search
              <Kbd shortcut="command-palette" />
            </TooltipContent>
          </Tooltip>
        </div>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={onSearch}
              tooltip={{
                children: (
                  <>
                    Search or jump to…
                    <Kbd shortcut="command-palette" />
                  </>
                ),
              }}
              // A quiet field on the canvas, which opens the palette rather than taking text. Its
              // gaps and its end are a step tighter than a nav row's, so the whole label fits
              // beside Ctrl K, which is wider than ⌘K; the keys never give way, the label would
              // truncate first.
              className="gap-1.5 pr-1.5 bg-background/60 font-normal text-muted-foreground ring-1 ring-sidebar-border hover:bg-background hover:text-foreground"
            >
              <SearchIcon />
              <span className="truncate">Search or jump to…</span>
              <span className="ml-auto shrink-0 group-data-[collapsible=icon]:hidden">
                <Kbd shortcut="command-palette" />
              </span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      {/* The pages, as the one navigation landmark, so a screen reader can reach or skip them. */}
      <SidebarContent role="navigation" aria-label="Main">
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {home && <DestinationItem destination={home} />}
              <InboxItem />
              {myTickets && <DestinationItem destination={myTickets} badge={openTickets} />}
              {shown.map((destination) =>
                destination.to === "/chat" ? (
                  <DestinationItem
                    key={destination.to}
                    destination={destination}
                    elsewhere={!onChat}
                  >
                    <ChatPages />
                  </DestinationItem>
                ) : (
                  <DestinationItem
                    key={destination.to}
                    destination={destination}
                    elsewhere={destination.to === "/projects" && onPersonal}
                  />
                ),
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <ChannelsSection />
        <DmsSection />
        <ProjectsSection projects={projects} myProject={myProject} />
      </SidebarContent>
      <Foot onShowShortcuts={onShowShortcuts} />
      <SidebarRail />
    </Sidebar>
  )
}
